import React, { useState, useEffect } from "react";
import {
  Share2,
  Heart,
  Phone,
  MessageCircle,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import Cookies from "js-cookie";
import {
  MdOutlineCalendarMonth,
  MdOutlinePhone,
  MdNoteAlt,
} from "react-icons/md";
import apiClient from "../common/apiClient";

const NewBuilderCard = (props) => {
  const navigate = useNavigate();
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isAutoPlay, setIsAutoPlay] = useState(true);
  const [isLoadingContact, setIsLoadingContact] = useState(false);
  const [contact, setContact] = useState(null); // { name, number }
  const [contactError, setContactError] = useState("");

  // Extract data from props based on the API response schema
  const {
    _id,
    propertyId,
    title,
    type,
    bhk = [],
    furnishedType,
    unitType,
    sqFt,
    age,
    floors,
    priceRange = { min: 0, max: 0 },
    address,
    area,
    city,
    description,
    media = [],
    propertyCurrentStatus,
    listedDate,
    name,
    number,
    brokerId,
    listedBy,
    dailyContact,
    occupencyType,
    bathrooms,
    verifyStatus,
    isDeleted,
    isSaved,
    isVerified,
    PossessionDate,
    createdOn,
    remarks,
  } = props;

  // Auto-play functionality for carousel
  useEffect(() => {
    if (media.length <= 1 || !isAutoPlay) return;

    const interval = setInterval(() => {
      setCurrentImageIndex((prevIndex) =>
        prevIndex === media.length - 1 ? 0 : prevIndex + 1
      );
    }, 4000); // Change image every 4 seconds

    return () => clearInterval(interval);
  }, [media.length, isAutoPlay]);

  // Function to handle card click and navigate to builder details
  const handleCardClick = () => {
    navigate("/builder-card", {
      state: {
        builderData: props,
      },
    });
  };
  const userId = Cookies.get("userId");

  const getContact = async (e) => {
    // prevent card click navigation
    if (e && typeof e.stopPropagation === "function") e.stopPropagation();
    const token = Cookies.get("token");

    try {
      setIsLoadingContact(true);
      setContactError("");

      const payload = {
        listedBy: listedBy || "builders",
        propId: _id || "",
        userId: userId, // replace with real user id if available
      };

      const response = await apiClient(
        "post",
        "/cjidnvij/ceksfbuebijn/user/broker-properties/contact",
        payload,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response.success) {
        throw new Error(response.error || "Failed to fetch contact");
      }

      const data = response.data;

      // Try common shapes; fall back to props if response doesn't include them
      const contactName =
        data?.name ||
        data?.contactName ||
        data?.data?.name ||
        name ||
        "Contact";
      const contactNumber =
        data?.number ||
        data?.contactNumber ||
        data?.data?.number ||
        number ||
        "";

      setContact({
        name: contactName,
        number: contactNumber?.toString().replace(/\s+/g, ""),
      });
    } catch (err) {
      setContactError("Could not fetch contact. Please try again.");
      console.error("Get contact error:", err);
    } finally {
      setIsLoadingContact(false);
    }
  };

  // Format date
  const formattedDate = listedDate
    ? new Date(listedDate).toLocaleDateString()
    : "";

  // Get current image URL or use default
 const currentImageUrl =
  media &&
  media.length > 0 &&
  media[currentImageIndex]?.url &&
  !media[currentImageIndex].url.toLowerCase().endsWith(".pdf")
    ? media[currentImageIndex].url.trim()
    : "/image/Image-not-available.png";


  // Format price range with lacs and crores
  const formatPrice = (price) => {
    if (!price || price === 0) return "";
    if (price >= 10000000) {
      // 1 crore or more
      const crValue = price / 10000000;
      return `₹${crValue % 1 === 0 ? Math.floor(crValue) : crValue.toFixed(2)
        }Cr`;
    } else if (price >= 100000) {
      // 1 lac or more
      const lacValue = price / 100000;
      return `₹${lacValue % 1 === 0 ? Math.floor(lacValue) : lacValue.toFixed(2)
        }Lac`;
    } else {
      return `₹${price.toLocaleString()}`;
    }
  };

  const minPrice = priceRange?.min ? priceRange.min : 0;
  const maxPrice = priceRange?.max ? priceRange.max : 0;
  const priceDisplay =
    minPrice && maxPrice
      ? `${formatPrice(minPrice)} - ${formatPrice(maxPrice)}`
      : minPrice
        ? `${formatPrice(minPrice)}+`
        : maxPrice
          ? `Up to ${formatPrice(maxPrice)}`
          : "Price on Request";

  // Image navigation functions
  const nextImage = (e) => {
    e?.stopPropagation();
    if (media.length > 0) {
      setCurrentImageIndex((prevIndex) =>
        prevIndex === media.length - 1 ? 0 : prevIndex + 1
      );
    }
    setIsAutoPlay(false); // Pause auto-play on manual navigation
  };

  const prevImage = (e) => {
    e?.stopPropagation();
    if (media.length > 0) {
      setCurrentImageIndex((prevIndex) =>
        prevIndex === 0 ? media.length - 1 : prevIndex - 1
      );
    }
    setIsAutoPlay(false); // Pause auto-play on manual navigation
  };

  const goToImage = (index, e) => {
    e?.stopPropagation();
    setCurrentImageIndex(index);
    setIsAutoPlay(false); // Pause auto-play on manual navigation
  };

  // Handle mouse enter/leave for auto-play
  const handleMouseEnter = () => setIsAutoPlay(false);
  const handleMouseLeave = () => setIsAutoPlay(true);

  return (
    <div
      className="bg-white rounded-xl shadow-md overflow-hidden hover:shadow-lg transition-shadow duration-300 w-full cursor-pointer"
      onClick={handleCardClick}
    >
      <div className="flex flex-col lg:flex-row">
        {/* Image Carousel Section */}
        <div
          className="relative lg:w-1/3 h-48 lg:h-[350px] overflow-hidden"
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
        >
          {/* Main Carousel Image */}
          <div className="relative w-full h-full">
            <img
              src={currentImageUrl}
              alt={`${title} - Image ${currentImageIndex + 1}`}
              className="w-full h-full object-cover transition-transform duration-500 ease-in-out transform hover:scale-105"
            />

            {/* Image Counter */}
            {media.length > 1 && (
              <div className="absolute top-3 right-3 bg-black/70 text-white px-2 py-1 rounded-full text-xs">
                {currentImageIndex + 1} / {media.length}
              </div>
            )}
          </div>

          {/* Navigation Dots */}
          {media.length > 1 && (
            <div className="absolute bottom-3 left-3 right-3 flex justify-center gap-1">
              {media.map((_, index) => (
                <button
                  key={index}
                  onClick={(e) => goToImage(index, e)}
                  className={`h-2 rounded-full transition-all duration-300 cursor-pointer ${index === currentImageIndex
                      ? "w-6 bg-white"
                      : "w-2 bg-white/50 hover:bg-white/70"
                    }`}
                />
              ))}
            </div>
          )}

          {/* Navigation Arrows */}
          {media.length > 1 && (
            <>
              <button
                className="absolute left-2 top-1/2 transform -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white p-2 rounded-full transition-all duration-300 hover:scale-110"
                onClick={prevImage}
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <button
                className="absolute right-2 top-1/2 transform -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white p-2 rounded-full transition-all duration-300 hover:scale-110"
                onClick={nextImage}
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </>
          )}

          {/* Thumbnail Strip for Multiple Images */}
          {media.length > 1 && (
            <div className="absolute bottom-12 left-3 right-3">
              <div className="flex gap-1 justify-center overflow-x-auto pb-2">
                {media.slice(0, 6).map((image, index) => (
                  <button
                    key={index}
                    onClick={(e) => goToImage(index, e)}
                    className={`flex-shrink-0 w-8 h-8 rounded border-2 transition-all duration-300 ${index === currentImageIndex
                        ? "border-white scale-110"
                        : "border-transparent hover:border-white/70"
                      }`}
                  >
                    <img
                      src={image.url}
                      alt={`Thumbnail ${index + 1}`}
                      className="w-full h-full object-cover rounded"
                    />
                  </button>
                ))}
                {media.length > 6 && (
                  <div className="flex-shrink-0 w-8 h-8 rounded bg-black/50 flex items-center justify-center text-white text-xs">
                    +{media.length - 6}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Content Section - Rest of your existing content */}
        <div className="flex-1 p-4 lg:p-6">
          {/* Header - ID and date in same line as title on mobile */}
          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-3 mb-4">
            <div className="flex-1">
              <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 mb-2">
                <div className="flex items-center justify-between w-full sm:w-auto">
                  <h2 className="text-xl lg:text-2xl font-bold text-gray-900 line-clamp-1 flex-1 sm:flex-none">
                    {title}
                  </h2>
                  {/* ID and Date badges - visible on mobile, hidden on desktop */}
                  <div className="flex gap-2 sm:hidden ml-2">
                    <div className="rounded-full text-red-500 font-medium px-2 py-1 border border-red-500 whitespace-nowrap text-xs">
                      {formattedDate}
                    </div>
                    <div className="rounded-full text-blue-600 font-medium px-2 py-1 border border-blue-600 whitespace-nowrap text-xs">
                      ID: {propertyId}
                    </div>
                  </div>
                </div>
                {/* ID and Date badges - hidden on mobile, visible on desktop */}
                <div className="hidden sm:flex gap-2 sm:ml-auto flex-wrap">
                  <div className="rounded-full text-red-500 font-medium px-2 py-1 border border-red-500 whitespace-nowrap text-xs sm:text-sm">
                    {formattedDate}
                  </div>
                  <div className="rounded-full text-blue-600 font-medium px-2 py-1 border border-blue-600 whitespace-nowrap text-xs sm:text-sm">
                    ID: {propertyId}
                  </div>
                </div>
              </div>
              <p className="text-sm text-gray-600 mb-2 flex flex-wrap items-center gap-1">
                {type && (
                  <>
                    <span>•</span> <span>{type}</span>
                  </>
                )}
                {bhk && bhk.length > 0 && (
                  <>
                    <span>•</span> <span>{bhk}</span>
                  </>
                )}
                {unitType && (
                  <>
                    <span>•</span> <span>{unitType}</span>
                  </>
                )}
              </p>
            </div>
            {/* Share button - Hidden on mobile, visible on desktop */}
            <div className="hidden sm:flex gap-2 sm:mt-0 self-end sm:self-auto">
              <button className="p-1.5 hover:bg-gray-100 rounded-full transition-colors">
                <Share2 className="w-4 h-4 text-gray-600" />
              </button>
            </div>
          </div>

          {/* Price and Info Grid */}
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4 mb-4 py-3 border-y border-gray-200">
            <div className="text-center">
              <div className="text-lg sm:text-xl lg:text-2xl font-bold text-gray-900">
                {priceDisplay}
              </div>
              <div className="text-xs text-gray-500 mt-1">Price Range</div>
            </div>
            <div className="text-center">
              <div className="text-base sm:text-lg font-semibold text-gray-900">
                {sqFt} SqFt
              </div>
              <div className="text-xs text-gray-500 mt-1">Size</div>
            </div>
            <div className="text-center">
              <div className="text-base sm:text-lg font-semibold text-gray-900">
                {furnishedType || "N/A"}
              </div>
              <div className="text-xs text-gray-500 mt-1">Furnished</div>
            </div>
            <div className="text-center">
              <div className="text-base sm:text-lg font-semibold text-gray-900">
                {propertyCurrentStatus || "N/A"}
              </div>
              <div className="text-xs text-gray-500 mt-1">Status</div>
            </div>
          </div>

          {/* Property Details and Contact in same line on desktop */}
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3">
            {/* Property Details */}
            <div className="flex-1">
              <h3 className="text-sm font-semibold text-gray-700 mb-2 flex items-center gap-1.5">
                <span className="text-[#503196]">⚡</span>
                Property Details
              </h3>
              <div className="flex items-center gap-2">
                <button className="text-gray-400 hover:text-gray-600 text-sm flex-shrink-0 hidden sm:block">
                  ←
                </button>
                <p className="text-sm text-gray-600 flex-1 line-clamp-2 sm:line-clamp-2">
                  {description || `${area}, ${city}`}
                </p>
                <button className="text-gray-400 hover:text-gray-600 text-sm flex-shrink-0 hidden sm:block">
                  →
                </button>
              </div>
            </div>

            {/* Contact */}
            <div className="mt-3">
              {/* Button (first screenshot style) */}
              {!contact && !contactError && (
                <button
                  onClick={getContact}
                  className="bg-[#503691] text-white px-4 py-2 rounded-full hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed"
                  disabled={isLoadingContact}
                >
                  {isLoadingContact ? "Loading..." : "Get Contact Number"}
                </button>
              )}

              {/* Error state */}
              {contactError && !contact && (
                <div
                  className="inline-block px-4 py-2 rounded-lg border border-red-300 text-red-700 bg-red-50 text-sm"
                  onClick={(e) => e.stopPropagation()}
                >
                  {contactError}
                </div>
              )}

              {/* Contact details chip (second screenshot style) */}
              {contact && (
                <div
                  className="flex items-center justify-between gap-3 py-2 px-4 rounded-lg bg-white text-[#503691] border border-gray-200"
                  onClick={(e) => e.stopPropagation()}
                >
                  <span className="text-[#503196] font-medium">
                    {contact.name} {contact.number ? `- ${contact.number}` : ""}
                  </span>

                  {/* call icon */}
                  {contact.number && (
                    <a
                      href={`tel:${contact.number}`}
                      className="text-[#503691] hover:text-blue-600 transition-colors"
                      onClick={(e) => e.stopPropagation()}
                      aria-label="Call"
                      title="Call"
                    >
                      <MdOutlinePhone className="h-6 w-6" />
                    </a>
                  )}

                  {/* WhatsApp icon */}
                  {contact.number && (
                    <a
                      href={`https://wa.me/${contact.number.replace(
                        /[^\d]/g,
                        ""
                      )}`}
                      target="_blank"
                      rel="noreferrer"
                      className="hover:scale-110 transition-transform"
                      onClick={(e) => e.stopPropagation()}
                      aria-label="WhatsApp"
                      title="WhatsApp"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 448 512"
                        className="h-6 w-6"
                        fill="#25D366"
                      >
                        <path d="M92.1 254.6c0 24.9 7 49.2 20.2 70.1l3.1 5-13.3 48.6L152 365.2l4.8 2.9c20.2 12 43.4 18.4 67.1 18.4h.1c72.6 0 133.3-59.1 133.3-131.8c0-35.2-15.2-68.3-40.1-93.2c-25-25-58-38.7-93.2-38.7c-72.7 0-131.8 59.1-131.9 131.8zM274.8 330c-12.6 1.9-22.4 .9-47.5-9.9c-36.8-15.9-61.8-51.5-66.9-58.7c-.4-.6-.7-.9-.8-1.1c-2-2.6-16.2-21.5-16.2-41c0-18.4 9-27.9 13.2-32.3c.3-.3 .5-.5 .7-.8c3.6-4 7.9-5 10.6-5c2.6 0 5.3 0 7.6 .1c.3 0 .5 0 .8 0c2.3 0 5.2 0 8.1 6.8c1.2 2.9 3 7.3 4.9 11.8c3.3 8 6.7 16.3 7.3 17.6c1 2 1.7 4.3 .3 6.9c-3.4 6.8-6.9 10.4-9.3 13c-3.1 3.2-4.5 4.7-2.3 8.6c15.3 26.3 30.6 35.4 53.9 47.1c4 2 6.3 1.7 8.6-1c2.3-2.6 9.9-11.6 12.5-15.5c2.6-4 5.3-3.3 8.9-2s23.1 10.9 27.1 12.9c.8 .4 1.5 .7 2.1 1c2.8 1.4 4.7 2.3 5.5 3.6c.9 1.9 .9 9.9-2.4 19.1c-3.3 9.3-19.1 17.7-26.7 18.8zM448 96c0-35.3-28.7-64-64-64H64C28.7 32 0 60.7 0 96V416c0 35.3 28.7 64 64 64H384c35.3 0 64-28.7 64-64V96zM148.1 393.9L64 416l22.5-82.2c-13.9-24-21.2-51.3-21.2-79.3C65.4 167.1 136.5 96 223.9 96c42.4 0 82.2 16.5 112.2 46.5c29.9 30 47.9 69.8 47.9 112.2c0 87.4-72.7 158.5-160.1 158.5c-26.6 0-52.7-6.7-75.8-19.3z" />
                      </svg>
                    </a>
                  )}
                </div>
              )}
                <p className="text-end">{dailyContact} people contacted this property today</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NewBuilderCard;
